import { apiRequest } from "./api";

// Maps a backend VehicleResponse onto the wizard's truck object shape
// ({ vin, cat, logging, suspended }), plus id (needed for edit/delete) and
// taxAmt (the backend's computed per-truck tax - never computed in React).
function fromVehicleResponse(v) {
  return {
    id: v.id,
    vin: v.vin,
    cat: v.vehicleCategoryCd,
    logging: !!v.isLogging,
    suspended: !!v.isSuspended,
    agri: !!v.isAgri,
    taxAmt: Number(v.taxAmt),
  };
}

function toVehicleRequestBody({ vin, cat, logging, suspended, agri }) {
  return {
    vin,
    vehicleCategoryCd: suspended ? "W" : cat,
    isLogging: !!logging,
    isSuspended: !!suspended,
    isAgri: !!suspended && !!agri,
  };
}

// Maps a backend AmendVehicleResponse onto the wizard's amendVehicles row
// shape ({ id, vin, oldCat, newCat, logging }) used by WeightIncreaseStep.
function fromAmendVehicleResponse(v) {
  return {
    id: v.id,
    vin: v.vin,
    oldCat: v.oldCategoryCd,
    newCat: v.newCategoryCd,
    logging: !!v.isLogging,
  };
}

// Maps a backend VinCorrectionResponse onto the wizard's correctedVins row
// shape ({ id, oldVin, vin, cat, logging, suspended }) used by VinCorrectionStep.
function fromVinCorrectionResponse(v) {
  return {
    id: v.id,
    oldVin: v.oldVin,
    vin: v.vin,
    cat: v.categoryCd,
    logging: !!v.isLogging,
    suspended: !!v.isSuspended,
  };
}

function toVinCorrectionRequestBody({ oldVin, vin, cat, logging, suspended }) {
  return {
    oldVin,
    vin,
    categoryCd: suspended ? "W" : cat,
    isLogging: !!logging,
    isSuspended: !!suspended,
  };
}

// Maps a backend TaxReturnResponse onto the wizard's return/totals shape.
// Exported so returnService.js (Tax Year / Form Type / Amended / VIN
// Correction steps) can reuse the exact same mapping instead of duplicating it.
export function fromReturnResponse(r) {
  return {
    returnId: r.id,
    businessId: r.businessId,
    year: r.taxYear,
    month: r.firstUsedMonth,
    status: r.status,
    trucks: (r.vehicles || []).map(fromVehicleResponse),
    totals: {
      reported: r.reportedVehicleCnt,
      taxable: r.taxableVehicleCnt,
      suspended: r.suspendedVehicleCnt,
      line2: Number(r.taxAmt),
      line3: Number(r.additionalTaxAmt) || 0,
      line4: Number(r.totalTaxAmt),
      line5: Number(r.creditsAmt) || 0,
      line6: Number(r.balanceDueAmt),
    },
    perCategory: (r.perCategory || []).map((p) => ({
      key: p.vehicleCategoryCd + (p.isLogging ? "-L" : ""),
      label: p.label,
      count: p.count,
      total: Number(p.lineAmt),
    })),
    // Form-type / amended-return / VIN-correction fields (Tax Year, Form,
    // Amended Return, Weight Increase, Mileage Exceeded, VIN Correction steps).
    returnType: r.formTypeCd || "",
    addressChange: !!r.addrChangeInd,
    amendReason: r.amendReasonCd || "",
    amendMonth: r.amendMonthWeight || "",
    amendMonthMileage: r.amendMonthMileage || "",
    vinCorrectionExpl: r.vinCorrectionExpl || "",
    amendVehicles: (r.amendVehicles || []).map(fromAmendVehicleResponse),
    correctedVins: (r.vinCorrections || []).map(fromVinCorrectionResponse),
    // Per-return preparer override (Form step's "Tax Preparer" dropdown) - null means this
    // return has none of its own (falls back to the business's default for display/PDF/XML).
    taxPreparerId: r.taxPreparerId ?? null,
    // Rejected-return-resubmission vehicle info (TrucksStep's "Vehicles: X/Y" note)
    // - display-only. originalVehicleCount/remainingVehicleCapacity/
    // extraVehiclesPendingFee are null unless rejectedResubmission is true AND the
    // count was actually captured (a rejected return that predates this field has
    // none of the three). extraVehiclesPendingFee > 0 means an additional service
    // fee is owed before this return can be resubmitted - the backend enforces
    // that for real at submit time (Return2290XmlServiceImpl.requireReturnIsPaidFor);
    // adding vehicles past originalVehicleCount is NOT blocked here or server-side
    // anymore (2026-08-17).
    rejectedResubmission: !!r.rejectedResubmission,
    originalVehicleCount: r.originalVehicleCount ?? null,
    remainingVehicleCapacity: r.remainingVehicleCapacity ?? null,
    extraVehiclesPendingFee: r.extraVehiclesPendingFee ?? null,
  };
}

// GET /businesses/{businessId}/return -> the caller's current draft return
// for this business (auto-created server-side if none exists yet), with its
// vehicles and server-computed totals.
export async function getReturnForBusiness(businessId) {
  const data = await apiRequest(`/businesses/${businessId}/return`);
  return fromReturnResponse(data);
}

// POST /businesses/{businessId}/return -> always creates a brand-new draft
// return for this business, regardless of any existing one. Used by
// Wizard.jsx when starting a genuinely new Amended/VIN Correction filing, so
// it never inherits vehicles left over on an unrelated draft (e.g. an
// abandoned Original filing for the same business) - see getReturnForBusiness
// above, which instead reuses whatever draft already exists.
export async function createFreshReturnForBusiness(businessId) {
  const data = await apiRequest(`/businesses/${businessId}/return`, { method: "POST" });
  return fromReturnResponse(data);
}

// GET /returns/{returnId} -> full detail for one specific return the caller
// owns, regardless of status (draft/submitted/accepted/rejected) - unlike
// getReturnForBusiness above, which only ever reuses a business's current
// draft. Backs My Filings' "View" action.
export async function getReturnById(returnId) {
  const data = await apiRequest(`/returns/${returnId}`);
  return fromReturnResponse(data);
}

// PATCH /returns/{returnId} -> update the first-used month; totals are
// recomputed server-side for the new month and returned.
export async function updateReturnMonth(returnId, month) {
  const data = await apiRequest(`/returns/${returnId}`, {
    method: "PATCH",
    body: { firstUsedMonth: month },
  });
  return fromReturnResponse(data);
}

// POST /returns/{returnId}/vehicles -> add a truck; returns the saved truck
// plus the return's recomputed totals.
export async function addVehicle(returnId, truck) {
  const data = await apiRequest(`/returns/${returnId}/vehicles`, {
    method: "POST",
    body: toVehicleRequestBody(truck),
  });
  return { truck: fromVehicleResponse(data.vehicle), ...fromReturnResponse(data.taxReturn) };
}

// POST /returns/{returnId}/vehicles/bulk -> adds each truck independently
// (partial success - a bad row doesn't undo rows already saved) in a
// single request instead of one call per row. Returns each row's outcome
// (matched back to the caller's row order via `index`) plus the return's
// totals recomputed once at the end.
export async function bulkAddVehicles(returnId, trucks) {
  const data = await apiRequest(`/returns/${returnId}/vehicles/bulk`, {
    method: "POST",
    body: { vehicles: trucks.map(toVehicleRequestBody) },
  });
  return {
    results: data.results.map((r) => ({
      index: r.index,
      ok: r.success,
      message: r.success ? undefined : r.errors?.[0]?.message || "Couldn't save this row.",
    })),
    ...fromReturnResponse(data.taxReturn),
  };
}

// PATCH /vehicles/{id} -> edit a truck; returns the saved truck plus the
// return's recomputed totals.
export async function updateVehicle(vehicleId, truck) {
  const data = await apiRequest(`/vehicles/${vehicleId}`, {
    method: "PATCH",
    body: toVehicleRequestBody(truck),
  });
  return { truck: fromVehicleResponse(data.vehicle), ...fromReturnResponse(data.taxReturn) };
}

// DELETE /vehicles/{id} -> remove a truck; returns the return's recomputed totals.
export async function deleteVehicle(vehicleId) {
  const data = await apiRequest(`/vehicles/${vehicleId}`, { method: "DELETE" });
  return fromReturnResponse(data);
}

// DELETE /returns/{returnId}/vehicles/bulk -> removes every listed truck in one
// transaction (all-or-nothing - unlike bulkAddVehicles, a bad id undoes nothing
// AND saves nothing, it's simply rejected). Returns the return's recomputed totals.
export async function deleteVehiclesBulk(returnId, vehicleIds) {
  const data = await apiRequest(`/returns/${returnId}/vehicles/bulk`, {
    method: "DELETE",
    body: { ids: vehicleIds },
  });
  return fromReturnResponse(data);
}

// POST /returns/{returnId}/vin-corrections -> add a corrected-VIN row;
// returns the saved row plus the return's recomputed totals.
export async function addVinCorrection(returnId, row) {
  const data = await apiRequest(`/returns/${returnId}/vin-corrections`, {
    method: "POST",
    body: toVinCorrectionRequestBody(row),
  });
  return { row: fromVinCorrectionResponse(data.vinCorrection), ...fromReturnResponse(data.taxReturn) };
}

// POST /returns/{returnId}/vin-corrections/bulk -> adds each corrected-VIN
// row independently (partial success) in a single request instead of one
// call per row - same result shape as bulkAddVehicles above.
export async function bulkAddVinCorrections(returnId, rows) {
  const data = await apiRequest(`/returns/${returnId}/vin-corrections/bulk`, {
    method: "POST",
    body: { vinCorrections: rows.map(toVinCorrectionRequestBody) },
  });
  return {
    results: data.results.map((r) => ({
      index: r.index,
      ok: r.success,
      message: r.success ? undefined : r.errors?.[0]?.message || "Couldn't save this row.",
    })),
    ...fromReturnResponse(data.taxReturn),
  };
}

// PATCH /vin-corrections/{id} -> edit a corrected-VIN row; returns the saved
// row plus the return's recomputed totals.
export async function updateVinCorrection(vinCorrectionId, row) {
  const data = await apiRequest(`/vin-corrections/${vinCorrectionId}`, {
    method: "PATCH",
    body: toVinCorrectionRequestBody(row),
  });
  return { row: fromVinCorrectionResponse(data.vinCorrection), ...fromReturnResponse(data.taxReturn) };
}

// DELETE /vin-corrections/{id} -> remove a corrected-VIN row; returns the
// return's recomputed totals.
export async function deleteVinCorrection(vinCorrectionId) {
  const data = await apiRequest(`/vin-corrections/${vinCorrectionId}`, { method: "DELETE" });
  return fromReturnResponse(data);
}

// DELETE /returns/{returnId}/vin-corrections/bulk -> removes every listed row in
// one transaction (all-or-nothing, same semantics as deleteVehiclesBulk above).
// Returns the return's recomputed totals.
export async function deleteVinCorrectionsBulk(returnId, vinCorrectionIds) {
  const data = await apiRequest(`/returns/${returnId}/vin-corrections/bulk`, {
    method: "DELETE",
    body: { ids: vinCorrectionIds },
  });
  return fromReturnResponse(data);
}
